import type { ReactiveController, ReactiveControllerHost } from 'lit'

import type DapDSNavigationMenuItem from './navigation-menu-item.component'

/** A reactive controller to manage submenu opening/closing with hover delay and safe triangle */
export class SubmenuController implements ReactiveController {
  private host: ReactiveControllerHost & DapDSNavigationMenuItem
  private enableSubmenuTimer = -1
  private isConnected = false
  private readonly submenuOpenDelay = 100

  constructor(host: ReactiveControllerHost & DapDSNavigationMenuItem) {
    ;(this.host = host).addController(this)
  }

  hostConnected() {
    if (this.hasSubmenu() && !this.host.disabled) {
      this.addListeners()
    }
  }

  hostDisconnected() {
    this.removeListeners()
  }

  hostUpdated() {
    if (this.hasSubmenu() && !this.host.disabled) {
      this.addListeners()
    } else {
      this.removeListeners()
    }
  }

  private hasSubmenu(): boolean {
    return this.host.hasNestedItems || this.host.hasContent
  }

  private addListeners() {
    if (!this.isConnected) {
      this.host.addEventListener('mousemove', this.handleMouseMove)
      this.host.addEventListener('mouseover', this.handleMouseOver)
      this.host.addEventListener('keydown', this.handleKeyDown)
      this.host.addEventListener('click', this.handleClick)
      this.host.addEventListener('focusout', this.handleFocusOut)
      this.isConnected = true
    }
  }

  private removeListeners() {
    if (this.isConnected) {
      this.host.removeEventListener('mousemove', this.handleMouseMove)
      this.host.removeEventListener('mouseover', this.handleMouseOver)
      this.host.removeEventListener('keydown', this.handleKeyDown)
      this.host.removeEventListener('click', this.handleClick)
      this.host.removeEventListener('focusout', this.handleFocusOut)
      this.isConnected = false
    }
  }

  // Set the safe triangle cursor position
  private handleMouseMove = (event: MouseEvent) => {
    this.host.style.setProperty(
      '--safe-triangle-cursor-x',
      `${event.clientX}px`,
    )
    this.host.style.setProperty(
      '--safe-triangle-cursor-y',
      `${event.clientY}px`,
    )
  }

  private handleMouseOver = () => {
    if (this.hasSubmenu()) {
      this.enableSubmenu(true) // With delay for mouse
    }
  }

  private handleSubmenuEntry(event: KeyboardEvent) {
    if (!this.hasSubmenu()) return

    // Open the submenu (if not open), and set focus to first menuitem
    event.preventDefault()
    event.stopPropagation()

    if (this.host.opened) {
      this.host.focusFirstDropdownChild()
    } else {
      this.enableSubmenu(false) // Instant for keyboard
      this.host.updateComplete.then(() => {
        this.host.focusFirstDropdownChild()
      })
    }
  }

  // Focus on the first menu-item of a submenu
  private handleKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement

    // Check if the event is coming from a nested navigation-menu-item
    // If so, don't handle it - let the nested item handle it
    const closestNavItem = target.closest('dap-ds-navigation-menu-item')
    if (closestNavItem && closestNavItem !== this.host) {
      // This is a nested item, don't handle the event
      return
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        event.stopPropagation()
        this.disableSubmenu()
        this.host.focus()
        break
      case 'Tab':
        this.disableSubmenu()
        break
      case 'ArrowLeft':
        // Either focus is currently on the host element or a child
        if (event.target !== this.host) {
          event.preventDefault()
          event.stopPropagation()
          this.host.focus()
          this.disableSubmenu()
        }
        break
      case 'ArrowRight':
        // Only handle ArrowRight for nested items (level > 0)
        // For top-level items (level 0), let parent handle navigation
        if (this.host.level > 0) {
          this.handleSubmenuEntry(event)
        }
        break
      case 'Enter':
      case ' ':
        // Only handle Enter/Space when focus is on the host's trigger element
        // Not when focus is on nested items or links inside nested items
        // Check if target is the host or is inside the host but not in a nested item
        if (
          target === this.host ||
          (this.host.contains(target) &&
            !target.closest('dap-ds-navigation-menu-item'))
        ) {
          this.handleSubmenuEntry(event)
        }
        break
      default:
        break
    }
  }

  private handleClick = (event: MouseEvent) => {
    // Clicking on the item which heads the menu does nothing, otherwise hide submenu and propagate
    if (event.target === this.host) {
      event.preventDefault()
      event.stopPropagation()
    } else if (
      event.target instanceof Element &&
      (event.target.tagName.toLowerCase() === 'dap-ds-navigation-menu-item' ||
        event.target.getAttribute('role')?.startsWith('menuitem'))
    ) {
      this.disableSubmenu()
    }
  }

  // Close this submenu on focus outside of the parent or any descendants
  private handleFocusOut = (event: FocusEvent) => {
    if (
      event.relatedTarget &&
      event.relatedTarget instanceof Element &&
      this.host.contains(event.relatedTarget)
    ) {
      return
    }
    this.disableSubmenu()
  }

  // Set the safe triangle values for the submenu when the position changes
  updateSafeTriangle = () => {
    const popup = this.host.popup
    if (!popup || !this.host.opened) {
      return
    }

    const { left, top, height } = popup.getBoundingClientRect()

    this.host.style.setProperty('--safe-triangle-submenu-start-x', `${left}px`)
    this.host.style.setProperty('--safe-triangle-submenu-start-y', `${top}px`)
    this.host.style.setProperty('--safe-triangle-submenu-end-x', `${left}px`)
    this.host.style.setProperty(
      '--safe-triangle-submenu-end-y',
      `${top + height}px`,
    )
  }

  private setSubmenuState(state: boolean, byHover = false) {
    if (state !== this.host.opened) {
      if (state) {
        this.host.showDropDown(byHover)
        // Update safe triangle after opening
        this.host.updateComplete.then(() => {
          this.updateSafeTriangle()
        })
      } else {
        this.host.hideDropDown()
      }
    }
  }

  // Shows the submenu. Supports disabling the opening delay for keyboard events
  private enableSubmenu(delay = true) {
    if (delay) {
      window.clearTimeout(this.enableSubmenuTimer)
      this.enableSubmenuTimer = window.setTimeout(() => {
        // Mark as opened by hover when using delay (mouse hover)
        this.setSubmenuState(true, true)
      }, this.submenuOpenDelay)
    } else {
      // Keyboard navigation - not opened by hover
      this.setSubmenuState(true, false)
    }
  }

  private disableSubmenu() {
    window.clearTimeout(this.enableSubmenuTimer)
    this.setSubmenuState(false)
  }

  // Public method to cancel pending submenu opening timer
  // Used when closing siblings to prevent race conditions
  public cancelPendingOpen() {
    window.clearTimeout(this.enableSubmenuTimer)
  }
}
